import { useEffect, useMemo } from 'react'
import { NATIVE_TOKEN_ADDRESS } from '@sirius-cybernetics-42/product-config'

import { getActiveChainId } from '@/config/active'
import { addressesEquivalent } from '@/data/marketApi/normalizeMarketToken'
import { usePoolsIdentity } from '@/data/marketApi/hooks/usePoolsIdentity'

import { Currency, Token } from '../../sdk'

import { useNutrimaticMarketOptional } from './NutrimaticMarketProvider'
import {
  candleSeriesToMarketCandles,
  formatNutrimaticPrice,
  formatNutrimaticUsd,
  formatPoolRowMetrics,
  tradeViewsToRecent,
} from './nutrimaticFormat'
import { useNutrimaticPairLive } from './useNutrimaticPairLive'

export type { MarketCandle, DepthLevel, RecentTrade } from '@/data/marketApi/types'

export type PairMarketData = {
  price: string
  change24h: string
  volume24h: string
  candles: import('@/data/marketApi/types').MarketCandle[]
  depth: { bids: import('@/data/marketApi/types').DepthLevel[]; asks: import('@/data/marketApi/types').DepthLevel[] }
  recentTrades: import('@/data/marketApi/types').RecentTrade[]
  loadOlder?: () => Promise<void>
  loadingOlder?: boolean
  chartInterval?: string
  setChartInterval?: (interval: import('@nutrimatic/sdk').CandleInterval) => void
}

const EMPTY: PairMarketData = {
  price: '—',
  change24h: '—',
  volume24h: '—',
  candles: [],
  depth: { bids: [], asks: [] },
  recentTrades: [],
}

function currencyAddress(currency: Currency): string {
  return currency instanceof Token ? currency.address.toLowerCase() : NATIVE_TOKEN_ADDRESS
}

export function usePairAddressForCurrencies(
  input?: Currency | null,
  output?: Currency | null,
): string | undefined {
  const { data: pools } = usePoolsIdentity()
  const hub = useNutrimaticMarketOptional()
  const chainId = getActiveChainId()

  return useMemo(() => {
    if (!input || !output) return undefined
    const inAddr = currencyAddress(input)
    const outAddr = currencyAddress(output)

    if (hub?.poolRows.length) {
      const row = hub.poolRows.find((r) => {
        const t0 = r.base?.onChainAddress?.toLowerCase() ?? NATIVE_TOKEN_ADDRESS
        const t1 = r.quote?.onChainAddress?.toLowerCase() ?? NATIVE_TOKEN_ADDRESS
        return (
          (addressesEquivalent(t0, inAddr, chainId) && addressesEquivalent(t1, outAddr, chainId)) ||
          (addressesEquivalent(t0, outAddr, chainId) && addressesEquivalent(t1, inAddr, chainId))
        )
      })
      if (row?.poolAddress) return row.poolAddress
    }

    if (!pools?.length) return undefined
    const pool = pools.find((p) => {
      const t0 = p.token0.address
      const t1 = p.token1.address
      return (
        (addressesEquivalent(t0, inAddr, chainId) && addressesEquivalent(t1, outAddr, chainId)) ||
        (addressesEquivalent(t0, outAddr, chainId) && addressesEquivalent(t1, inAddr, chainId))
      )
    })
    return pool?.pairAddress
  }, [input, output, pools, hub?.poolRows, chainId])
}

export function usePairMarketData(input?: Currency | null, output?: Currency | null): PairMarketData {
  const pairAddress = usePairAddressForCurrencies(input, output)
  const hub = useNutrimaticMarketOptional()
  const live = useNutrimaticPairLive(pairAddress)

  useEffect(() => {
    if (!hub || !pairAddress) return
    if (hub.selected && hub.poolRows.some((r) => r.poolAddress === pairAddress.toLowerCase() && r.listing.venueId === hub.selected?.venueId)) {
      return
    }
    hub.selectPairByPoolAddress(pairAddress)
  }, [hub, pairAddress])

  const hubMatches =
    Boolean(hub?.selected) &&
    Boolean(pairAddress) &&
    hub?.poolRows.some(
      (r) =>
        r.poolAddress === pairAddress?.toLowerCase() && r.listing.venueId === hub.selected?.venueId,
    )

  return useMemo(() => {
    if (!pairAddress) return EMPTY

    if (hubMatches && hub) {
      const metrics = formatPoolRowMetrics(hub.detail.pool)
      const tradeEvents =
        hub.detail.liveTradesHead?.events ?? hub.detail.trades?.events ?? []
      return {
        price: formatNutrimaticPrice(hub.detail.spot) !== '—'
          ? formatNutrimaticPrice(hub.detail.spot)
          : metrics.price,
        change24h: metrics.change24h,
        volume24h: metrics.volume24h !== '—' ? metrics.volume24h : formatNutrimaticUsd(hub.detail.pool?.volume24hUsd),
        candles: candleSeriesToMarketCandles(hub.detail.chart),
        depth: { bids: [], asks: [] },
        recentTrades: tradeViewsToRecent(tradeEvents),
        loadOlder: hub.loadOlderCandles,
        loadingOlder: hub.detail.loadingOlder,
        chartInterval: hub.chartInterval,
        setChartInterval: hub.setChartInterval,
      }
    }

    return {
      price: live.price,
      change24h: live.change24h,
      volume24h: live.volume24h,
      candles: live.candles,
      depth: { bids: [], asks: [] },
      recentTrades: live.recentTrades,
      loadOlder: live.loadOlder,
      loadingOlder: live.loadingOlder,
    }
  }, [
    pairAddress,
    hubMatches,
    hub,
    live.price,
    live.change24h,
    live.volume24h,
    live.candles,
    live.recentTrades,
    live.loadOlder,
    live.loadingOlder,
  ])
}
